import { Stack, IconButton, Tooltip, alpha } from '@mui/material';
import { Visibility, ModeEdit, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { MouseEvent } from 'react';
import type { HubService, ServiceHubType } from '../../constants/serviceHubs';

interface ServiceQuickActionsProps {
  service: Pick<HubService, '_id' | 'slug' | 'name' | 'isSystem' | 'themeColor'>;
  hubType: ServiceHubType;
  onEdit?: (service: HubService) => void;
  onView?: (service: HubService) => void;
  onDelete?: (id: string) => void;
}

const actionButtonSx = {
  width: 32,
  height: 32,
  borderRadius: 1.5,
  border: 1,
  borderColor: 'divider',
  '& svg': { fontSize: 16, width: 16, height: 16 },
};

export default function ServiceQuickActions({
  service,
  hubType,
  onEdit,
  onView,
  onDelete,
}: ServiceQuickActionsProps) {
  const navigate = useNavigate();
  const color = service.themeColor || '#2563eb';

  const handleView = (e: MouseEvent) => {
    e.stopPropagation();
    if (onView) {
      onView(service as HubService);
      return;
    }
    navigate(`/hubs/${hubType}/${service.slug}/masters`);
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip title="View service" arrow>
        <IconButton
          size="small"
          onClick={handleView}
          sx={{
            ...actionButtonSx,
            color,
            bgcolor: alpha(color, 0.08),
            '&:hover': {
              bgcolor: alpha(color, 0.14),
              borderColor: alpha(color, 0.35),
            },
          }}
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>

      {onEdit && (
        <Tooltip title="Edit service" arrow>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onEdit(service as HubService); }}
            sx={actionButtonSx}
          >
            <ModeEdit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {!service.isSystem && onDelete && (
        <Tooltip title="Delete service" arrow>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => { e.stopPropagation(); onDelete(service._id); }}
            sx={{ ...actionButtonSx, borderColor: alpha('#d32f2f', 0.3) }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
